import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ArrowRightLeft, CheckCircle2, Clock, Info, QrCode, RefreshCw, XCircle } from 'lucide-react';
import type { PropertyStatus } from '@/domain/entities/property';

import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PropertyMemberRole } from '@/domain/entities/property-member';
import {
  fetchPropertyMemberProfile,
  fetchPropertyMembers,
  selectPropertyMembersByPropertyID,
  selectPropertyMembersLoadingByPropertyID,
} from '@/features/property-members';
import { selectPropertyHandoverState } from '@/features/properties/propertiesSlice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

function formatTs(ts?: number) {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleString();
}

function statusMeta(status?: PropertyStatus) {
  switch (status) {
    case 'builderDraft':
      return { label: 'Builder draft', icon: Info, variant: 'secondary' as const };
    case 'readyForHandover':
      return { label: 'Ready for handover', icon: ArrowRightLeft, variant: 'default' as const };
    case 'handoverInitiated':
      return { label: 'Handover initiated', icon: Clock, variant: 'outline' as const };
    case 'owned':
      return { label: 'Owned', icon: CheckCircle2, variant: 'default' as const };
    default:
      return { label: 'Not set', icon: Info, variant: 'outline' as const };
  }
}

function isExpired(expiresAt?: number) {
  if (!expiresAt) return false;
  return expiresAt * 1000 <= Date.now();
}

function getOwnerDisplayName(
  profile?: { firstName?: string; lastName?: string; phone?: string; email?: string } | null,
) {
  if (!profile) return null;

  const fullName = `${profile.firstName?.trim() ?? ''} ${profile.lastName?.trim() ?? ''}`.trim();
  return fullName || profile.phone?.trim() || profile.email?.trim() || null;
}

export function PropertyStatusBox(props: {
  propertyId: string;
  propertyCode?: string;
  status?: PropertyStatus;
  ownedSince?: number;
  isBuilderContext: boolean;
  canManageHandover?: boolean;
  canReadMembers?: boolean;
  onInitiateTransfer: () => void;
  onViewHandover: () => void;
  onCancelHandover?: () => void;
  onRegenerateQr?: () => void;
}) {
  const dispatch = useAppDispatch();
  const {
    propertyId,
    propertyCode,
    status,
    ownedSince,
    isBuilderContext,
    canManageHandover = false,
    canReadMembers = false,
    onInitiateTransfer,
    onViewHandover,
    onCancelHandover,
    onRegenerateQr,
  } = props;

  const s = statusMeta(status);
  const Icon = s.icon;

  const canInitiate =
    canManageHandover && isBuilderContext && (status === 'builderDraft' || status === 'readyForHandover');

  // ✅ Store-driven handover details
  const handover = useAppSelector((st) => selectPropertyHandoverState(st, propertyId));
  const code = handover.data?.handover?.handoverCode;
  const expiresAt = handover.data?.handover?.expiresAt;
  const initiatedAt = handover.data?.handover?.initiatedAt;
  const expired = isExpired(expiresAt);
  const members = useAppSelector(selectPropertyMembersByPropertyID(propertyId));
  const isLoadingMembers = useAppSelector(selectPropertyMembersLoadingByPropertyID(propertyId));
  const profilesByUserID = useAppSelector((st) => st.propertyMembers.profilesByUserID ?? {});
  const ownerMember = useMemo(
    () => members.find((member) => member.role === PropertyMemberRole.Owner && member.userID?.trim()),
    [members],
  );
  const ownerUserID = ownerMember?.userID?.trim();
  const ownerProfile = ownerUserID ? profilesByUserID[ownerUserID] : null;
  const ownerLabel = getOwnerDisplayName(ownerProfile) || ownerUserID;

  useEffect(() => {
    if (!canReadMembers || !propertyId) return;
    dispatch(fetchPropertyMembers({ propertyID: propertyId }));
  }, [canReadMembers, dispatch, propertyId]);

  useEffect(() => {
    if (!canReadMembers || !ownerUserID || !ownerMember?.profileType) return;
    if (profilesByUserID[ownerUserID]) return;
    dispatch(fetchPropertyMemberProfile({ userID: ownerUserID, profileType: ownerMember.profileType }));
  }, [canReadMembers, dispatch, ownerMember?.profileType, ownerUserID, profilesByUserID]);

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Icon className="text-muted-foreground h-4 w-4" />
            <CardTitle className="text-base">Property status</CardTitle>
            <Badge variant={s.variant}>{s.label}</Badge>

            {/* ✅ extra badge when handover is expired */}
            {status === 'handoverInitiated' && expired ? <Badge variant="destructive">Expired</Badge> : null}
          </div>

          {/* ✅ Right-side actions */}
          {canInitiate ? (
            <Button size="sm" onClick={onInitiateTransfer}>
              Initiate transfer
            </Button>
          ) : canManageHandover && status === 'handoverInitiated' ? (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={onViewHandover} className="gap-2">
                <QrCode className="h-4 w-4" />
                View QR
              </Button>

              {onRegenerateQr ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={onRegenerateQr}
                  disabled={!expired && Boolean(code)}
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  {expired ? 'Regenerate' : 'Regenerate'}
                </Button>
              ) : null}

              {onCancelHandover ? (
                <Button size="sm" variant="destructive" onClick={onCancelHandover} className="gap-2">
                  <XCircle className="h-4 w-4" />
                  Cancel
                </Button>
              ) : null}
            </div>
          ) : status === 'owned' ? (
            <Button size="sm" variant="secondary" disabled>
              Already owned
            </Button>
          ) : null}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {propertyCode ? (
          <div>
            <div className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Property code</div>
            <div className="font-mono text-2xl font-bold tracking-[0.2em] sm:text-3xl">{propertyCode}</div>
          </div>
        ) : null}

        <p className="text-muted-foreground text-sm">
          Ownership handover allows transferring this property to a buyer once it is ready.
        </p>

        <Separator />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div>
            <div className="text-muted-foreground text-xs">Owner</div>
            <div className="text-sm font-medium">
              {!canReadMembers ? (
                'Restricted'
              ) : ownerUserID && ownerLabel && ownerMember?.profileType ? (
                <Button asChild variant="link" className="h-auto p-0 text-sm font-medium">
                  <Link
                    to={
                      ownerMember?.profileType === 'business'
                        ? `/admin/users?userId=${encodeURIComponent(ownerUserID)}`
                        : `/admin/homeowners/${encodeURIComponent(ownerUserID)}`
                    }
                  >
                    {ownerLabel}
                  </Link>
                </Button>
              ) : ownerUserID ? (
                ownerUserID
              ) : isLoadingMembers ? (
                'Loading...'
              ) : (
                '—'
              )}
            </div>
          </div>

          <div>
            <div className="text-muted-foreground text-xs">
              {status === 'owned' ? 'Owned since' : 'Transfer status'}
            </div>
            <div className="text-sm font-medium">
              {status === 'owned'
                ? formatTs(ownedSince)
                : status === 'handoverInitiated'
                  ? expired
                    ? 'Expired'
                    : 'Pending acceptance'
                  : status
                    ? 'Not initiated'
                    : 'Not set'}
            </div>
          </div>

          <div>
            <div className="text-muted-foreground text-xs">Handover code</div>
            <div className="text-sm font-medium">{canManageHandover ? (code ?? '—') : 'Restricted'}</div>
          </div>

          <div>
            <div className="text-muted-foreground text-xs">Expires at</div>
            <div className="text-sm font-medium">
              {canManageHandover ? (expiresAt ? formatTs(expiresAt) : '—') : 'Restricted'}
            </div>
          </div>
        </div>

        {/* optional: initiated at */}
        {canManageHandover && status === 'handoverInitiated' ? (
          <div className="text-muted-foreground text-xs">Initiated at: {formatTs(initiatedAt)}</div>
        ) : null}

        {/* optional: store error */}
        {canManageHandover && handover.error ? (
          <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-lg border p-3 text-sm whitespace-pre-wrap">
            {handover.error}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
